import { useMemo, useState, useRef } from 'react';

import {
    useLhsRiskTypesQuery,
    useWorkOrderRequirementApprovalReasonsQuery,
} from 'common/stores/redux/MetaStore/MetaStoreApi';
import * as logger from 'common/utils/Logger';
import Alert from 'common/components/Alert';
import { useAppSelector } from 'common/stores/redux/hooks';
import { getFileUploadErrorMessage } from '../../../file-upload/FileUploadTypes';

const usePhotoRequirementsData = () => {
    const { data: workOrderRequirementApprovalReasons } =
        useWorkOrderRequirementApprovalReasonsQuery();
    const { data: lhsRiskTypes } = useLhsRiskTypesQuery();

    return {
        workOrderRequirementApprovalReasons,
        lhsRiskTypes,
    };
};

const usePhotoUpload = () => {
    const [acceptedFiles, setAcceptedFiles] = useState([]);
    const [action, setAction] = useState();
    const photoRef = useRef(null);

    const handleOnDrop = (
        newAcceptedFiles,
        rejectedFiles,
        newAction,
        allowFiles
    ) => {
        if (
            newAction === 'reupload' &&
            photoRef.current?.requirement?.mimeType &&
            newAcceptedFiles.length > 0
        ) {
            const originalMime = photoRef.current.requirement.mimeType;
            const newMime = newAcceptedFiles[0].type;

            const originalIsVideo = originalMime.startsWith('video/');
            const originalIsImage = originalMime.startsWith('image/');

            const newIsVideo = newMime.startsWith('video/');
            const newIsImage = newMime.startsWith('image/');

            const isTypeMismatch =
                (originalIsVideo && !newIsVideo) ||
                (originalIsImage && !newIsImage);

            if (isTypeMismatch) {
                Alert.error(
                    originalIsVideo
                        ? 'Please re-upload a video file only. The original file was a video.'
                        : 'Please re-upload an image file only. The original file was an image.',
                    { autoClose: false }
                );
                cleanUpFiles(newAcceptedFiles);
                return;
            }
        }

        setAcceptedFiles(newAcceptedFiles);
        setAction(newAction);
        if (rejectedFiles.length > 0) {
            Alert.warning(
                allowFiles
                    ? getFileUploadErrorMessage(allowFiles)
                    : 'The file you attempted to upload was not a valid image',
                { autoClose: false }
            );
            cleanUpFiles(rejectedFiles);
        }
    };

    const handleCloseUpload = () => {
        cleanUpFiles(acceptedFiles);
        setAcceptedFiles([]);
        setAction(undefined);
    };

    const cleanUpFiles = files => {
        try {
            files.forEach(file => {
                window.URL.revokeObjectURL(file.preview);
            });
        } catch (error) {
            logger.error(error);
        }
    };

    return {
        acceptedFiles,
        setAcceptedFiles,
        action,
        setAction,
        handleOnDrop,
        handleCloseUpload,
        photoRef,
    };
};

const useApprovalWorkflow = () => {
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [showEditPhotoRequirementsModal, setShowEditPhotoRequirementsModal] =
        useState(false);
    const [expandApprovalHistory, setExpandApprovalHistory] = useState(false);
    const [approvalHistory, setApprovalHistory] = useState(null);

    const handleModalOpen = statusId => {
        setShowApprovalModal(statusId);
    };

    const handleModalClose = () => {
        setShowApprovalModal(false);
    };

    const clearApprovalHistory = () => {
        setApprovalHistory(null);
        setExpandApprovalHistory(false);
    };

    const handleExpandedApprovalHistory = async (
        photo,
        expand,
        getApprovalHistoryFn
    ) => {
        setExpandApprovalHistory(expand);
        if (!approvalHistory && expand) {
            try {
                const history = await getApprovalHistoryFn(photo.id);
                setApprovalHistory(history);
            } catch (error) {
                logger.error(error);
            }
        }
    };

    return {
        showApprovalModal,
        showEditPhotoRequirementsModal,
        setShowEditPhotoRequirementsModal,
        expandApprovalHistory,
        approvalHistory,
        setApprovalHistory,
        handleModalOpen,
        handleModalClose,
        clearApprovalHistory,
        handleExpandedApprovalHistory,
    };
};

const useUserPermissions = photo => {
    const { token, isMotiliAccount, isContractorAccount } = useAppSelector(
        state => state.authentication
    );

    return useMemo(() => {
        const authenticatedUserId = token?.userId;
        const authenticatedUserIsOps = isMotiliAccount;
        const authenticatedUserIsCon = isContractorAccount;

        const photoUploadedByAuthenticatedUser =
            photo && photo.userId === authenticatedUserId;

        const authenticatedUserCanEdit =
            authenticatedUserIsOps ||
            photoUploadedByAuthenticatedUser ||
            authenticatedUserIsCon;

        return {
            authenticatedUserCanEdit,
            authenticatedUserIsCon,
            authenticatedUserIsOps,
        };
    }, [photo]);
};

export {
    usePhotoRequirementsData,
    usePhotoUpload,
    useApprovalWorkflow,
    useUserPermissions,
};
